import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { TrendingUp, Mail, Lock, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function ForgotPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    
    try {
      await api.post('/api/auth/forgot-password', { email });
      setSuccess('If an account exists with this email, you will receive a password reset link.');
    } catch (error) {
      setError('Something went wrong. Please try again.');
    }
    
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await api.post('/api/auth/reset-password', {
        token,
        new_password: newPassword
      });
      setSuccess('Password reset successfully! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Invalid or expired reset token');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 bg-[#007AFF] rounded-sm flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-white" />
          </div>
          <span className="font-bold text-xl tracking-tight text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
            COSSY NETWORK
          </span>
        </Link>
        
        {/* Form Card */}
        <div className="bg-[#141417] border border-[#27272A] rounded-md p-8">
          <Link to="/login" className="inline-flex items-center gap-2 text-[#71717A] hover:text-white mb-6 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to login
          </Link>
          
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {token ? 'Reset Password' : 'Forgot Password'}
            </h1>
            <p className="text-[#71717A]">
              {token ? 'Enter your new password' : 'Enter your email to receive a reset link'}
            </p>
          </div>
          
          {error && (
            <div className="bg-[#FF3333]/10 border border-[#FF3333]/30 text-[#FF3333] px-4 py-3 rounded-sm mb-6 text-sm">
              {error}
            </div>
          )}
          
          {success && (
            <div className="bg-[#00FF66]/10 border border-[#00FF66]/30 text-[#00FF66] px-4 py-3 rounded-sm mb-6 text-sm">
              {success}
            </div>
          )}
          
          {token ? (
            <form onSubmit={handleResetPassword} className="space-y-5">
              <div>
                <Label htmlFor="newPassword" className="text-[#A1A1AA]">New Password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="input-dark pl-10"
                    required
                    data-testid="new-password-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="confirmPassword" className="text-[#A1A1AA]">Confirm Password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="input-dark pl-10"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                className="btn-primary w-full"
                disabled={loading}
                data-testid="reset-password-btn"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-[#A1A1AA]">Email</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="input-dark pl-10"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                className="btn-primary w-full"
                disabled={loading}
                data-testid="send-reset-link-btn"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
